import { call, put, select } from "redux-saga/effects";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import {
  EntityType,
  EntityTypesSupportingFieldCustomization,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import localSettings from "@app/config/LocalSettings";
import {
  areMileageTrackingAmountsVisible,
  doesOrganizationSupportMultiContactActivities,
  doesOrganizationSupportTerritories,
  getDefaultColumns,
  getFeatures,
  getMe,
} from "@app/store/iam";
import { fetchMe } from "@app/store/iam/actions";
import { isManager, isOwner } from "@app/store/iam/util";
import { getEntitySchemaFields } from "@app/store/schema";
import Iam from "@app/types/Iam";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import dealFieldModel from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType, {
  EntityTypeWithFieldModel,
} from "@app/util/fieldModel/getByEntityType";
import personFieldModel from "@app/util/fieldModel/PersonFieldModel";
import tripFieldModel from "@app/util/fieldModel/tripFieldModel";
import setupSourceObjectField from "@app/util/fieldModel/util/setupSourceObjectField";
import tripAmountsFieldFilter from "@app/util/fieldModel/util/tripAmountsFieldFilter";
import loggingService from "@app/util/logging";

type FieldFilter = (field: IField) => boolean;

const colorFieldFilter: FieldFilter = (field) => !field.hasFeature(FieldFeature.COLOR_FIELD);
const territoryFieldFilter: FieldFilter = (field) =>
  !field.hasFeature(FieldFeature.TERRITORY_FIELD);
const singleContactFieldFilter: FieldFilter = (field) =>
  !field.hasFeature(FieldFeature.SINGLE_CONTACT_ONLY);
const multiContactFieldFilter: FieldFilter = (field) =>
  !field.hasFeature(FieldFeature.MULTI_CONTACT_ONLY);

const ENTITY_TYPES_WITH_SCHEMA: EntityTypesSupportingFieldCustomization[] = [
  EntityType.COMPANY,
  EntityType.PERSON,
  EntityType.DEAL,
  EntityType.ACTIVITY,
];

// owners and managers are allowed to see these fields in lists and to filter by them
const ROLE_GATED_ACTIVITY_FIELDS = [
  ActivityFieldName.RELIABILITY,
  ActivityFieldName.CHECK_IN_DISTANCE,
];

// What each role-gated field had before any owner or manager unlocked it, restored on a demotion
const defaultNonListView = new Map<IField, boolean>();

// The models are singletons that outlive an access change, so a filter is always removed before
// it is re-added: a re-run neither stacks copies nor keeps a filter whose feature changed.
const setFieldFilter = (
  fieldModels: Pick<IFieldModel, "addFieldFilter" | "removeFieldFilter">[],
  filter: FieldFilter,
  isApplied: boolean
) => {
  fieldModels.forEach((fieldModel) => {
    fieldModel.removeFieldFilter(filter);
    if (isApplied) {
      fieldModel.addFieldFilter(filter);
    }
  });
};

// Local overrides let devs try org features without changing org data
export function* applyFeaturesOverride() {
  const hasFeaturesOverride: boolean = yield call(localSettings.hasFeaturesOverride);
  if (!hasFeaturesOverride) {
    return;
  }

  const me: Iam = yield select(getMe);
  const features: OrganizationMetaData["features"] = yield select(getFeatures);
  const overriddenFeatures: OrganizationMetaData["features"] = yield call(
    localSettings.getFeaturesOverride
  );
  yield put(
    fetchMe.success({
      ...me,
      organization: {
        ...me.organization,
        metaData: { ...me.organization.metaData, features: { ...features, ...overriddenFeatures } },
      },
    })
  );
}

export function* syncFieldModelFilters() {
  const me: Iam = yield select(getMe);
  const features: OrganizationMetaData["features"] = yield select(getFeatures);
  const territoriesEnabled: boolean = yield select(doesOrganizationSupportTerritories);
  const isMultiContactEnabled: boolean = yield select(
    doesOrganizationSupportMultiContactActivities
  );
  const mileageTrackingValuesVisible: boolean = yield select(areMileageTrackingAmountsVisible);

  setFieldFilter(
    [companyFieldModel, personFieldModel],
    colorFieldFilter,
    !!features?.[OrganizationFeature.DISALLOW_COLOR]?.enabled
  );
  setFieldFilter(
    [companyFieldModel, personFieldModel, dealFieldModel],
    territoryFieldFilter,
    !territoriesEnabled
  );
  setupSourceObjectField(companyFieldModel, EntityType.COMPANY, me.organization.sourceObjects);
  setupSourceObjectField(personFieldModel, EntityType.PERSON, me.organization.sourceObjects);
  setFieldFilter([activityFieldModel], singleContactFieldFilter, isMultiContactEnabled);
  setFieldFilter([activityFieldModel], multiContactFieldFilter, !isMultiContactEnabled);
  setFieldFilter([tripFieldModel], tripAmountsFieldFilter, !mileageTrackingValuesVisible);
}

// setSchema only reaches the fields the filters let through, so it must run after the filters
export function* syncFieldModelSchemas() {
  for (const entityType of ENTITY_TYPES_WITH_SCHEMA) {
    const fieldModel: IFieldModel = getFieldModelByEntityType(
      entityType as EntityTypeWithFieldModel
    );
    const schema: SchemaField[] = yield select(getEntitySchemaFields, entityType);
    fieldModel.setSchema(schema);
  }
}

export function* syncRoleGatedFieldFeatures() {
  const me: Iam = yield select(getMe);
  const isUnlocked = isOwner(me) || isManager(me);
  ROLE_GATED_ACTIVITY_FIELDS.forEach((fieldName) => {
    const field = activityFieldModel.getByName(fieldName);
    if (!field) {
      return;
    }
    if (!defaultNonListView.has(field)) {
      defaultNonListView.set(field, field.hasFeature(FieldFeature.NON_LIST_VIEW));
    }
    field.toggleFeature(FieldFeature.NON_LIST_VIEW, !isUnlocked && !!defaultNonListView.get(field));
  });
}

// Custom fields come back as new field objects on every load, so this re-runs after them
export function* applyDefaultColumns() {
  const defaultColumns: Exclude<OrganizationMetaData["defaultColumns"], undefined> =
    yield select(getDefaultColumns);
  Object.keys(defaultColumns).forEach((entityType) => {
    const fieldModel = getFieldModelByEntityType(entityType as EntityTypeWithFieldModel);
    if (!fieldModel) {
      loggingService.debug("Invalid entity type in default columns", entityType);
      return;
    }

    const defaultFields =
      defaultColumns[entityType as keyof Exclude<OrganizationMetaData["defaultColumns"], undefined>]
        ?.map((platformFieldName) => fieldModel.getByPlatformName(platformFieldName))
        .filter(isDefined)
        .map((field) => field.name) ?? [];

    if (defaultFields.length) {
      fieldModel.fields.forEach((field) => {
        field.toggleFeature(
          FieldFeature.VISIBLE_BY_DEFAULT,
          field.hasFeature(FieldFeature.ALWAYS_VISIBLE) || defaultFields.includes(field.name)
        );
      });
    }
  });
}
